import { useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import RefreshIcon from '@mui/icons-material/Refresh';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { describeApiError } from '../../../shared/api/errors';
import type { CompanyNotaFiscalEvento, CompanyNotaFiscalListItem } from './types';
import type { CompanyNotasFiscaisHooks } from './hooks';

function statusChipColor(status: string): 'success' | 'error' | 'warning' | 'info' | 'default' {
  const s = status.toUpperCase();
  if (s === 'AUT' || s === 'OK') return 'success';
  if (s === 'EER' || s === 'REJ') return 'error';
  if (s === 'PEN') return 'warning';
  if (s === 'ENV') return 'info';
  return 'default';
}

function statusIcon(status: string): JSX.Element {
  const color = statusChipColor(status);
  if (color === 'success') return <CheckCircleIcon fontSize="small" />;
  if (color === 'error') return <ErrorOutlineIcon fontSize="small" />;
  return <HourglassEmptyIcon fontSize="small" />;
}

function tryPrettyJson(raw: string | null): string {
  if (!raw) return '';
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
}

export interface NotaFiscalEventosDialogProps {
  open: boolean;
  nota: CompanyNotaFiscalListItem | null;
  hooks: CompanyNotasFiscaisHooks;
  onClose: () => void;
}

export function NotaFiscalEventosDialog({
  open,
  nota,
  hooks,
  onClose,
}: NotaFiscalEventosDialogProps): JSX.Element {
  const { t } = useTranslation();
  const query = hooks.useEventos(open ? (nota?.id ?? null) : null, nota?.empresaId);
  const [expandedPayload, setExpandedPayload] = useState<string | null>(null);

  const eventos = query.data ?? [];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>
          {t('features.empresa.notasFiscais.eventos.title', {
            numero: nota?.numero ?? `#${nota?.id ?? ''}`,
          })}
        </span>
        <IconButton
          size="small"
          onClick={() => void query.refetch()}
          disabled={query.isFetching}
          aria-label={t('features.empresa.notasFiscais.eventos.refresh')}
        >
          <RefreshIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {query.isLoading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : query.isError ? (
          <Alert severity="error">{describeApiError(query.error)}</Alert>
        ) : eventos.length === 0 ? (
          <Alert severity="info">{t('features.empresa.notasFiscais.eventos.vazio')}</Alert>
        ) : (
          <Stack spacing={1.5}>
            {eventos.map((ev: CompanyNotaFiscalEvento, idx) => (
              <Box key={ev.id ?? idx}>
                <Stack direction="row" spacing={1.5} alignItems="flex-start">
                  <Box sx={{ pt: 0.5 }}>{statusIcon(ev.status)}</Box>
                  <Box flex={1}>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                      <Chip
                        size="small"
                        color={statusChipColor(ev.status)}
                        label={ev.status}
                        variant={statusChipColor(ev.status) === 'success' ? 'filled' : 'outlined'}
                      />
                      <Chip size="small" variant="outlined" label={ev.tipo} />
                      {ev.codigo && (
                        <Typography variant="caption" color="text.secondary">
                          {t('features.empresa.notasFiscais.eventos.codigo')}: {ev.codigo}
                        </Typography>
                      )}
                      <Box flex={1} />
                      <Typography variant="caption" color="text.secondary">
                        {dayjs(ev.dataHora).isValid()
                          ? dayjs(ev.dataHora).format('DD/MM/YYYY HH:mm:ss')
                          : ev.dataHora}
                      </Typography>
                    </Stack>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      {ev.mensagem}
                    </Typography>
                    {ev.payload && (
                      <Accordion
                        disableGutters
                        elevation={0}
                        expanded={expandedPayload === (ev.id ?? String(idx))}
                        onChange={(_, exp) =>
                          setExpandedPayload(exp ? (ev.id ?? String(idx)) : null)
                        }
                        sx={{ mt: 0.5, '&:before': { display: 'none' }, bgcolor: 'transparent' }}
                      >
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon />}
                          sx={{ minHeight: 32, px: 0 }}
                        >
                          <Typography variant="caption" color="text.secondary">
                            {t('features.empresa.notasFiscais.eventos.payloadLabel')}
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ px: 0, pt: 0 }}>
                          <Box
                            component="pre"
                            sx={{
                              m: 0,
                              p: 1,
                              fontSize: 12,
                              bgcolor: 'action.hover',
                              borderRadius: 1,
                              overflow: 'auto',
                              maxHeight: 240,
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word',
                            }}
                          >
                            {tryPrettyJson(ev.payload)}
                          </Box>
                        </AccordionDetails>
                      </Accordion>
                    )}
                  </Box>
                </Stack>
                {idx < eventos.length - 1 && <Divider sx={{ mt: 1.5 }} />}
              </Box>
            ))}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.close')}</Button>
      </DialogActions>
    </Dialog>
  );
}
